import { FC, useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';

import { useModalStore } from '@/store/use-modal-store';
import { useUnifiedAccount } from '@/hooks';
import { truncateAddress } from '@/utils/format';
import { bigIntToNum, formatBigInt } from '@/utils/value-format';
import {
  COLLATERAL_DECIMALS,
  COLLATERAL_SYMBOL,
} from '@/store/use-markets-store';
import { useAccount, useBalance, useSwitchChain } from 'wagmi';
import {
  estimateGas,
  getGasPrice,
  readContract,
  writeContract,
} from '@wagmi/core';
import { SOLANA_CHAIN_ID, wagmiConfig } from '@/lib/wagmi/config';
import BRIDGE_ABI from '@/lib/abis/src-bridge.json';
import { getBridgeAddress } from '@/utils/addresses';
import { NetworkType } from '@/types';
import { toast } from 'sonner';
import { arbitrum } from 'viem/chains';
import { getErrorMessage } from '@/utils/dev';
import { encodeFunctionData, keccak256 } from 'viem';
import { Spinner } from '@/components/ui/spinner';

export const WithdrawConfirmationModal: FC = () => {
  const {
    isWithdrawConfirmationModalOpen,
    handleWithdrawConfirmationModalOpen,
    setHandledVerifiedTransferID,
    verifiedTransfer,
  } = useModalStore();
  const { address, networkType, chain: activeChain } = useUnifiedAccount();
  const { chain, isConnected, connector, address: ethAddress } = useAccount();
  const { chains, switchChain } = useSwitchChain();

  const [isEnoughGas, setIsEnoughGas] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    // if (networkType === NetworkType.SOLANA && chain?.id != arbitrum.id) {
    //   switchChain({ chainId: arbitrum.id });
    // }

    if (verifiedTransfer) {
      const { chainId } = verifiedTransfer;

      switchChain({
        chainId: chainId === SOLANA_CHAIN_ID ? arbitrum.id : chainId,
      });
    }
  }, [chain, isConnected, networkType, verifiedTransfer]);

  const { data: ethBalance } = useBalance({
    address: ethAddress,
  });

  useEffect(() => {
    const getEstimatedGas = async () => {
      if (!isConnected || !verifiedTransfer) return;
      setIsLoading(true);
      setIsEnoughGas(false);

      try {
        const {
          chainId,
          requestArgs,
          signer,
          signature,
          signatureProof,
          validatorSignature,
        } = verifiedTransfer;

        const requestHash = keccak256(requestArgs as `0x${string}`);

        const completedTransactions: number[] = (await readContract(
          wagmiConfig,
          {
            abi: BRIDGE_ABI,
            address: getBridgeAddress(chainId),
            functionName: 'getCompletedTransactions',
            args: [[requestHash]],
          },
        )) as unknown as number[];

        if (completedTransactions[0]) {
          toast.warning('Request already filled');
          handleWithdrawConfirmationModalOpen(false);
          return;
        }

        const functionName =
          networkType === NetworkType.SOLANA ? 'withdrawSolana' : 'withdraw';

        const data = encodeFunctionData({
          abi: BRIDGE_ABI,
          functionName,
          args: [
            requestArgs,
            signer,
            signature,
            signatureProof,
            validatorSignature,
            networkType === NetworkType.SOLANA,
          ],
        });

        const estimatedGas = await estimateGas(wagmiConfig, {
          to: getBridgeAddress(chainId),
          data,
          connector,
        });

        const gasPrice = await getGasPrice(wagmiConfig);
        const totalGasCost = estimatedGas * gasPrice;

        setIsEnoughGas(
          bigIntToNum(totalGasCost, ethBalance?.decimals ?? 18) <=
            Number(ethBalance?.formatted),
        );
      } catch (err: any) {
        console.error(err);

        toast.error('Unexpected error. Please contact support on Discord.');
        handleWithdrawConfirmationModalOpen(false);
        return;
      }

      setIsLoading(false);
    };
    if (isWithdrawConfirmationModalOpen) getEstimatedGas();
  }, [
    verifiedTransfer,
    ethBalance,
    isConnected,
    chain,
    isWithdrawConfirmationModalOpen,
  ]);

  const handleWithdraw = async () => {
    if (!verifiedTransfer) return;

    try {
      const {
        id,
        chainId,
        requestArgs,
        signer,
        signature,
        signatureProof,
        validatorSignature,
      } = verifiedTransfer;

      const functionName =
        networkType === NetworkType.SOLANA ? 'withdrawSolana' : 'withdraw';

      if (networkType === NetworkType.SOLANA) {
        if (!isConnected) {
          toast.warning(
            'To withdraw from Solana Mainnet Beta, at least one EVM wallet should be connected.',
          );
          return;
        }

        if (chain?.id != arbitrum.id) {
          toast.warning(
            'To withdraw from Solana Mainnet Beta, active network of EVM wallet should be arbitrum.',
          );
          return;
        }
      }

      const promise = writeContract(wagmiConfig, {
        abi: BRIDGE_ABI,
        address: getBridgeAddress(chainId),
        functionName,
        args: [
          requestArgs,
          signer,
          signature,
          signatureProof,
          validatorSignature,
          networkType === NetworkType.SOLANA,
        ],
      }).then(() => setHandledVerifiedTransferID(id));

      toast.promise(promise, {
        loading: 'Processing withdrawal...',
        success: 'Transaction sent!',
        error: (err) => {
          const message =
            err?.shortMessage || err?.message || 'Withdraw failed';
          console.error('Contract error:', err);
          return `Error: ${message}`;
        },
      });

      handleWithdrawConfirmationModalOpen(false);
    } catch (error: unknown) {
      console.error(error);
      if (error instanceof Error || typeof error === 'string') {
        const reasonString = getErrorMessage(error);

        toast.error(reasonString);
      }
    }
  };

  const verifiedChains = chains.filter(
    (chain) => chain.id == verifiedTransfer?.chainId,
  );
  return (
    <Dialog
      open={isWithdrawConfirmationModalOpen}
      onOpenChange={handleWithdrawConfirmationModalOpen}
    >
      <DialogContent className="gap-0 border border-border bg-background p-0 sm:max-w-md sm:rounded-none">
        <DialogHeader className="px-5 pt-6">
          <DialogTitle className="text-xl text-foreground">
            Withdrawal Approved
          </DialogTitle>
        </DialogHeader>

        <div className="relative p-5 text-lg text-vestgrey-100">
          <div className="pb-3">
            A validator has approved your withdrawal request. <br />
            Click the button below to complete the transaction from your wallet.
          </div>
          <div className="text-md">
            <div className="flex justify-between">
              <span>- Amount</span>
              <span>
                {formatBigInt(verifiedTransfer?.size, COLLATERAL_DECIMALS, {
                  digits: 2,
                  round: 'ceil',
                })}{' '}
                {COLLATERAL_SYMBOL}
              </span>
            </div>
            <div className="flex justify-between">
              <span>- Recipient</span>
              <span> {truncateAddress(address || '')}</span>
            </div>
            <div className="flex justify-between">
              <span>- Destination</span>
              <span>
                {verifiedTransfer?.chainId == 0
                  ? 'Solana Mainnet Beta'
                  : verifiedChains.length > 0
                    ? verifiedChains[0].name
                    : activeChain?.name}
              </span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            disabled={!isEnoughGas || isLoading}
            className="h-14 w-full rounded-none font-mono text-lg font-normal tracking-wider"
            onClick={handleWithdraw}
          >
            {isLoading ? (
              <Spinner />
            ) : isEnoughGas ? (
              'WITHDRAW'
            ) : (
              'INSUFFICIENT ETH FOR GAS FEE'
            )}
          </Button>
          {/* <Button
            type="button"
            onClick={() => handleWithdrawConfirmationModalOpen(false)}
            className="m-0 h-14 w-full rounded-none bg-black font-mono text-lg font-normal uppercase tracking-wider text-foreground hover:bg-black"
          >
            Cancel
          </Button> */}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
